import { useState } from 'react';
import { Inbox, RefreshCw } from 'lucide-react';
import type { TableProps } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';

import { DataTable, MenuThaoTac } from '../../../components/shared';
import { Button, Empty } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayGioVN } from '../../../lib/dinh-dang';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import {
  BadgeLoaiToken,
  BadgeTrangThaiYeuCau,
  NoiDungYeuCauToken,
} from '../../tenant/components/NoiDungYeuCauToken';
import { useHuyYeuCauToken, useYeuCauToken } from '../../tenant/hooks/useCapThemToken';
import { daDuYeuCauCho } from '../../tenant/nghiep-vu/cap-them-token';
import type { AiTokenRequest } from '../../tenant/types';
import { HopGuiYeuCauToken } from './HopGuiYeuCauToken';

/**
 * Yêu cầu cấp thêm token AI của tổ chức, ở thẻ "Token AI tháng này" của cổng tenant (ADR-0091 mục 3): nút
 * "Gửi yêu cầu", danh sách yêu cầu kèm trạng thái và phản hồi của quản trị nền tảng, huỷ yêu cầu đang chờ.
 * Chỉ Tenant Admin thấy (thẻ cha đã lọc vai trò).
 */
export function YeuCauTokenToChuc({ tenantId }: { tenantId: string }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const q = useYeuCauToken(tenantId);
  const huy = useHuyYeuCauToken(tenantId);
  const [moGui, setMoGui] = useState(false);
  const [canHuy, setCanHuy] = useState<AiTokenRequest | null>(null);

  const ds = q.data ?? [];
  const nutGui = (
    <Button variant="outline" size="sm" onClick={() => setMoGui(true)}>
      {t('tokenAi.guiYeuCau')}
    </Button>
  );

  const columns: TableProps<AiTokenRequest>['columns'] = [
    { title: t('tokenAi.cotLoai'), width: 130, render: (_v, row) => <BadgeLoaiToken loai={row.kind} /> },
    {
      title: t('chung.trangThai'),
      width: 200,
      render: (_v, row) => (
        <div className="flex min-w-0 max-w-[12rem] flex-col items-start gap-1">
          <BadgeTrangThaiYeuCau trangThai={row.status} />
          {row.decision_note && (
            <span className="line-clamp-2 text-2xs text-content-muted" title={row.decision_note}>
              {t('tokenAi.phanHoi', { noiDung: row.decision_note })}
            </span>
          )}
        </div>
      ),
    },
    { title: t('tokenAi.cotNoiDung'), width: 240, render: (_v, row) => <NoiDungYeuCauToken r={row} /> },
    {
      title: t('tokenAi.cotGuiLuc'),
      width: 150,
      render: (_v, row) => (
        <span className="whitespace-nowrap text-xs tabular-nums text-content-secondary">
          {ngayGioVN(row.requested_at, locale)}
        </span>
      ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, row) =>
        row.status === 'PENDING' ? (
          <MenuThaoTac
            muc={[
              {
                khoa: 'huy',
                nhan: t('tokenAi.huyYeuCau'),
                icon: <CloseCircleOutlined />,
                nhom: 'pha-huy',
                onChon: () => {
                  huy.reset();
                  setCanHuy(row);
                },
              },
            ]}
          />
        ) : null,
    },
  ];

  return (
    <section aria-labelledby="yeu-cau-token-tieu-de" className="space-y-2 border-t border-default pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="yeu-cau-token-tieu-de" className="text-sm font-semibold text-content-primary">
          {t('tokenAi.yeuCauCuaToChuc')}
        </h3>
        {ds.length > 0 && nutGui}
      </div>

      {q.error && !q.data ? (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3">
          <span className="min-w-0 text-sm text-danger-700">{t('tokenAi.loiYeuCau', { loi: dichLoi(q.error) })}</span>
          <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
            <RefreshCw size={13} />
            {t('chung.thuLai')}
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-default">
          <DataTable<AiTokenRequest>
            caption={t('tokenAi.yeuCauCaption')}
            rowKey="id"
            dataSource={ds}
            columns={columns}
            loading={q.isLoading}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={false}
            locale={{
              emptyText: q.isLoading ? (
                ' '
              ) : (
                <Empty
                  icon={Inbox}
                  title={t('tokenAi.yeuCauRong')}
                  description={t('tokenAi.yeuCauRongMo')}
                  action={nutGui}
                />
              ),
            }}
          />
        </div>
      )}

      {moGui && (
        <HopGuiYeuCauToken tenantId={tenantId} daDuCho={daDuYeuCauCho(ds)} onClose={() => setMoGui(false)} />
      )}
      <HopXacNhanThaoTac
        open={Boolean(canHuy)}
        tieuDe={t('tokenAi.xacNhanHuy')}
        than={t('tokenAi.xacNhanHuyMo')}
        nhanXacNhan={t('tokenAi.huyYeuCau')}
        nguyHiem
        dangChay={huy.isPending}
        loi={huy.error ? dichLoi(huy.error) : null}
        onClose={() => setCanHuy(null)}
        onXacNhan={() => canHuy && huy.mutate(canHuy.id, { onSuccess: () => setCanHuy(null) })}
      />
    </section>
  );
}
